// EmployeeFormModal.jsx
// ONE Add / Edit employee form, shared by Admin Employee and Admin Dashboard.
// It owns the form state, validation, saving and the success / error alerts.
//
//   <EmployeeFormModal
//     open={bool}
//     employee={null | emp}      // null = Add, emp row from /api/employee-list = Edit
//     employees={[...]}          // existing rows, used for the "code already taken" check
//     onClose={() => ...}
//     onSaved={(result) => ...}  // called after a successful save (reload your lists here)
//   />
import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import './EmployeeModal.css';

/* ── API base, chosen by the site you are on ─────────────────── */
export const getApiBase = () => {
  const host = window.location.hostname;
  if (host === 'portalstaging.manurevasolutions.com' || host === 'www.portalstaging.manurevasolutions.com') {
    return 'https://api.leave-management.manurevasolutions.com';
  }
  if (host === 'leave-portal.manurevasolutions.com' || host === 'www.leave-portal.manurevasolutions.com') {
    return 'https://api.leave-portal.manurevasolutions.com';
  }
  if (host === 'localhost' || host === '127.0.0.1') {
    return process.env.REACT_APP_API_URL || 'http://localhost:5000';
  }
  return process.env.REACT_APP_API_URL || 'https://api.leave-portal.manurevasolutions.com';
};

const apiFetch = (path, opts = {}) =>
  fetch(`${getApiBase()}${path}`, { credentials: 'include', headers: { 'Accept': 'application/json' }, ...opts });

/* ── shared helpers (also used by AdminEmployee) ─────────────── */
export const getInitials = (name) =>
  name?.trim() ? name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) : 'A';

export const escapeHtml = (s) =>
  String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const ALERT_ICONS = {
  success: { bg: '#f0fdf4', border: '#bbf7d0', stroke: '#16a34a', body: '<polyline points="20 6 9 17 4 12"/>' },
  error:   { bg: '#fef2f2', border: '#fecaca', stroke: '#ef4444', body: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>' },
  warning: { bg: '#fffbeb', border: '#fde68a', stroke: '#f59e0b', body: '<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>' },
};

export const showAlert = ({ type = 'success', title, messageHtml, confirmText = 'OK', confirmColor = '#111111', showCancel = false, cancelText = 'Cancel' }) => {
  const i = ALERT_ICONS[type];
  return Swal.fire({
    html: `
      <div style="padding:1rem 0.5rem">
        <div style="width:56px;height:56px;border-radius:50%;background:${i.bg};border:1.5px solid ${i.border};display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="${i.stroke}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">${i.body}</svg>
        </div>
        <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">${title}</p>
        <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">${messageHtml}</p>
      </div>`,
    width: '340px',
    showCancelButton: showCancel,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    confirmButtonColor: confirmColor,
    customClass: {
      popup: 'swal-employee rounded-2xl',
      actions: 'gap-2',
      confirmButton: 'rounded-sm text-sm font-medium px-6',
      cancelButton: 'rounded-sm text-sm font-medium px-6 bg-white text-gray-500 border border-gray-200',
    },
  });
};

/* ── constants ───────────────────────────────────────────────── */
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
  'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
  'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
  'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana',
  'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
  'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Central African Republic',
  'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
  'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea',
  'Estonia', 'Eswatini', 'Ethiopia',
  'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada',
  'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary',
  'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
  'Jamaica', 'Japan', 'Jordan',
  'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein',
  'Lithuania', 'Luxembourg',
  'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
  'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
  'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique',
  'Myanmar',
  'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua',
  'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
  'Oman',
  'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay',
  'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar',
  'Romania', 'Russia', 'Rwanda',
  'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal',
  'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
  'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
  'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
  'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo',
  'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Uruguay', 'Uzbekistan',
  'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
  'Yemen',
  'Zambia', 'Zimbabwe',
];

// 28 states + 8 union territories
const INDIAN_STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar',
  'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka',
  'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
  'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
];

const ROLE_OPTIONS = [
  ['employee', 'Employee'], ['manager', 'Manager'], ['hr', 'HR'], ['ceo', 'CEO'],
  ['consultant', 'Consultant'], ['superadmin', 'Superadmin'],
  ['network_manager', 'Network Manager'], ['housekeeping', 'Housekeeping'],
];
const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS);
const MANAGED_ROLES = ['employee', 'housekeeping', 'network_manager'];

const DEPT_OPTIONS = [
  ['engineering', 'Engineering'], ['hr', 'Human Resources'], ['sales', 'Sales'],
  ['marketing', 'Marketing'], ['finance', 'Finance'], ['executive', 'Executive'],
];
const DEPT_LABEL = Object.fromEntries(DEPT_OPTIONS);

// Old spellings stored in DB -> current names used in the dropdown
const LEGACY_STATE_NAMES = {
  pondicherry: 'Puducherry', pondy: 'Puducherry', orissa: 'Odisha',
  uttaranchal: 'Uttarakhand', 'new delhi': 'Delhi', 'nct of delhi': 'Delhi',
};
const normalizeState = (s) => LEGACY_STATE_NAMES[s?.trim().toLowerCase()] || s?.trim() || '';

const EMPTY_FORM = {
  fullName: '', email: '', phoneNumber: '', alternatePhone: '', address: '',
  employeeCode: '', join_date: '', role: '', department: '', manager: '',
  location: '', state: '',
};

// Employee row (from /api/employee-list) -> form values
const employeeToForm = (emp) => {
  const toInputDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('/')) {
      const [day, month, year] = dateStr.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr.split('T')[0];
  };
  const country = emp.location || '';
  // Older rows stored the country in the state column - don't show "India" as a state
  const state = emp.state && emp.state !== country
    ? (country === 'India' ? normalizeState(emp.state) : emp.state)
    : '';
  return {
    fullName: emp.name || '', email: emp.email || '',
    phoneNumber: emp.ContactNumber1 || emp.contact_number_1 || '',
    alternatePhone: emp.ContactNumber2 || emp.contact_number_2 || '',
    address: emp.address || '',
    role: emp.role?.toLowerCase().replace(/\s+/g, '_') || '',
    department: emp.department?.toLowerCase() || '',
    manager: emp.manager_id || '',
    location: country,
    state,
    employeeCode: emp.employeeid || '',
    join_date: toInputDate(emp.joinDate || emp.join_date || ''),
  };
};

/* ── small icons ─────────────────────────────────────────────── */
const ErrIcon = () => (
  <svg width="12" height="12" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
export const CheckIcon = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
export const CloseIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/* ── form field wrapper ──────────────────────────────────────── */
const Field = ({ label, htmlFor, required, error, hint, ok, full, children }) => (
  <div className={`emp-field${full ? ' emp-field--full' : ''}`}>
    <label className="emp-label" htmlFor={htmlFor}>
      {label}{required && <span className="emp-req" aria-hidden="true">*</span>}
    </label>
    {children}
    {error
      ? <span className="emp-error" role="alert"><ErrIcon />{error}</span>
      : ok
        ? <span className="emp-ok"><CheckIcon />{ok}</span>
        : hint ? <span className="emp-hint">{hint}</span> : null}
  </div>
);

/* ── searchable dropdown (country / Indian state) ────────────── */
const SearchSelect = ({ id, value, options, onChange, placeholder, searchPlaceholder, emptyText, hasError }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const list = options.filter(o => o.toLowerCase().includes(q));
  const pick = (o) => { onChange(o); setOpen(false); setQuery(''); };

  return (
    <div className="emp-ss" ref={ref}>
      <button
        id={id}
        type="button"
        className={`emp-input emp-ss-trigger${hasError ? ' has-error' : ''}${open ? ' is-open' : ''}`}
        onClick={() => { setOpen(o => !o); setQuery(''); }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={value ? '' : 'emp-placeholder'}>{value || placeholder}</span>
        <svg className="emp-ss-chevron" width="16" height="16" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9l6 6 6-6" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="emp-ss-panel">
          <div className="emp-ss-search">
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="8" stroke="#94a3b8" strokeWidth="2" />
              <path d="M21 21l-4.35-4.35" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              autoFocus
              type="text"
              value={query}
              placeholder={searchPlaceholder}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && list.length) { e.preventDefault(); pick(list[0]); }
                if (e.key === 'Escape') { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); setOpen(false); }
              }}
            />
          </div>
          <ul className="emp-ss-list" role="listbox">
            {list.map(o => (
              <li
                key={o}
                role="option"
                aria-selected={o === value}
                className={`emp-ss-option${o === value ? ' is-selected' : ''}`}
                onClick={() => pick(o)}
              >
                {o}
                {o === value && <CheckIcon size={14} />}
              </li>
            ))}
            {list.length === 0 && <li className="emp-ss-empty">{emptyText}</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   The modal
   ═══════════════════════════════════════════════════════════════ */
const EmployeeFormModal = ({ open, employee = null, employees = [], onClose, onSaved }) => {
  const editMode = !!employee;
  const editId   = employee?.id ?? null;

  const [form, setForm]             = useState(EMPTY_FORM);
  const [formErr, setFormErr]       = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [managers, setManagers]     = useState([]);

  // Reset the form every time the modal opens
  useEffect(() => {
    if (!open) return;
    setForm(employee ? employeeToForm(employee) : EMPTY_FORM);
    setFormErr({});
  }, [open, employee]);

  // Load managers / HR for the "reporting manager" dropdown when it opens
  useEffect(() => {
    if (!open) return;
    let alive = true;
    apiFetch('/api/managers-list')
      .then(r => (r.ok ? r.json() : []))
      .then(list => { if (alive) setManagers(Array.isArray(list) ? list : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [open]);

  // Escape closes + lock page scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      // Swal handles its own Esc - don't also close the form underneath it
      if (e.key !== 'Escape' || submitting || Swal.isVisible()) return;
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, submitting, onClose]);

  if (!open) return null;

  /* ── helpers ─────────────────────────────────────────────── */
  const clearErr = (name) => { if (formErr[name]) setFormErr(p => ({ ...p, [name]: '' })); };

  const onChange = (e) => {
    const { name, value } = e.target;
    const isPhoneField = name === 'phoneNumber' || name === 'alternatePhone';
    setForm(p => ({ ...p, [name]: isPhoneField ? value.replace(/\D/g, '') : value }));
    clearErr(name);
  };

  const onCodeChange = (e) => {
    // Always "MA" + digits. Typing a digit into an empty box gives "MA1".
    const cleaned = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const rest = cleaned.startsWith('MA') ? cleaned.slice(2) : cleaned.replace(/^M/, '');
    setForm(p => ({ ...p, employeeCode: 'MA' + rest.replace(/\D/g, '') }));
    clearErr('employeeCode');
  };

  const onRoleChange = (e) => {
    const next = e.target.value;
    // Employee / Housekeeping / Network Manager share the same manager list,
    // so keep the selected manager when switching between them.
    setForm(p => ({
      ...p,
      role: next,
      manager: MANAGED_ROLES.includes(p.role) && MANAGED_ROLES.includes(next) ? p.manager : '',
    }));
    clearErr('role');
  };

  const onCountryChange = (country) => {
    // Changing country resets the state, since the old state won't belong to it
    setForm(p => ({ ...p, location: country, state: p.location === country ? p.state : '' }));
    clearErr('location');
  };

  const onStateChange = (value) => {
    setForm(p => ({ ...p, state: value }));
    clearErr('state');
  };

  const isCodeTaken = (code) => {
    const c = code?.trim().toLowerCase();
    if (!c) return false;
    return employees.some(emp =>
      (!editMode || emp.id !== editId) &&
      emp.employeeid?.toString().trim().toLowerCase() === c
    );
  };

  const validate = () => {
    const e = {};

    if (!form.fullName?.trim()) e.fullName = 'Name is required';

    if (!form.email?.trim()) {
      e.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = 'Invalid email format';
    } else {
      const email = form.email.trim().toLowerCase();
      if (!email.endsWith('@manurevasolutions.net') && !email.endsWith('@manurevasolutions.com')) {
        e.email = 'Only official Manureva Solutions email addresses are allowed';
      }
    }

    if (!form.employeeCode || form.employeeCode === 'MA') e.employeeCode = 'Employee code is required (e.g. MA001)';
    else if (!/^MA[0-9]+$/.test(form.employeeCode))       e.employeeCode = 'Must be MA followed by numbers only (e.g. MA001)';
    else if (isCodeTaken(form.employeeCode))              e.employeeCode = 'This employee code already exists';

    if (!form.join_date || !String(form.join_date).trim()) e.join_date = 'Joining date is required';

    if (!form.phoneNumber?.trim()) e.phoneNumber = 'Phone number is required';
    else if (!/^\d{10}$/.test(form.phoneNumber)) e.phoneNumber = 'Must be 10 digits';
    if (form.alternatePhone?.trim() && !/^\d{10}$/.test(form.alternatePhone)) e.alternatePhone = 'Must be 10 digits';

    if (!form.role)             e.role       = 'Role is required';
    if (!form.department)       e.department = 'Department is required';
    if (!form.address?.trim())  e.address    = 'Address is required';
    if (!form.location?.trim()) e.location   = 'Country is required';
    if (!form.state?.trim())    e.state      = form.location ? 'State is required' : 'Select a country first';

    setFormErr(e);
    return Object.keys(e).length === 0;
  };

  const closeForm = () => { if (!submitting) onClose?.(); };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const body = {
        name: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        ContactNumber1: form.phoneNumber,
        ContactNumber2: form.alternatePhone?.trim() ? form.alternatePhone : null,
        address: form.address.trim(),
        role: form.role.charAt(0).toUpperCase() + form.role.slice(1),
        department: form.department.charAt(0).toUpperCase() + form.department.slice(1),
        manager_id: form.manager || null,
        location: form.location.trim(),
        state: form.state.trim(),
        employeeid: form.employeeCode.trim(),
        join_date: form.join_date.trim(),
        status: 'active',
      };

      const res = await apiFetch(editMode ? `/api/update-employee/${editId}` : '/api/create-employee', {
        method: editMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to save employee');

      if (result.action === 'reactivated') {
        showAlert({ title: 'Account reactivated', messageHtml: escapeHtml(result.message || 'Employee reactivated.'), confirmText: 'Perfect' });
      } else {
        showAlert({
          title: editMode ? 'Employee updated' : 'Employee created',
          messageHtml: editMode ? 'The employee details were updated.' : 'The employee can now log in.',
          confirmColor: '#16a34a',
        });
      }

      setForm(EMPTY_FORM);
      onClose?.();
      onSaved?.(result);
    } catch (err) {
      const msg = err.message || 'Failed to save employee profile.';
      if (msg.includes('already exists')) {
        showAlert({ type: 'warning', title: 'Already exists', messageHtml: escapeHtml(msg), confirmText: 'Check details' });
      } else {
        showAlert({ type: 'error', title: 'Could not save', messageHtml: escapeHtml(msg), confirmText: 'Close' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* ── derived values ──────────────────────────────────────── */
  const codeComplete = /^MA\d+$/.test(form.employeeCode || '');
  const codeTaken    = codeComplete && isCodeTaken(form.employeeCode);
  const assignableManagers = managers.filter(m => m.role === 'Manager' || m.role === 'Superadmin');
  const hrFromManagers     = managers.filter(m => m.role === 'HR');
  // managers-list may not include HR users - fall back to the employee list
  const assignableHR = hrFromManagers.length ? hrFromManagers : employees.filter(m => m.role === 'HR');
  const showManager  = MANAGED_ROLES.includes(form.role);
  const showHR       = form.role === 'consultant';
  const isIndia      = form.location === 'India';
  const inputCls     = (name, extra = '') => `emp-input${formErr[name] ? ' has-error' : ''}${extra ? ' ' + extra : ''}`;

  return (
    // No backdrop-click close: a stray click would throw away the typed form
    <div className="emp-modal-overlay">
      <div className="emp-modal" role="dialog" aria-modal="true" aria-labelledby="emp-form-title">

        {/* Header previews the employee as you type */}
        <header className="emp-modal-header">
          <div className="emp-avatar" aria-hidden="true">{getInitials(form.fullName)}</div>
          <div className="emp-modal-heading">
            <p className="emp-modal-kicker">{editMode ? 'Edit employee' : 'Add new employee'}</p>
            <h2 id="emp-form-title" className={`emp-modal-title${form.fullName.trim() ? '' : ' is-placeholder'}`}>
              {form.fullName.trim() || 'New team member'}
            </h2>
            {(codeComplete || form.role || form.department) && (
              <div className="emp-modal-meta">
                {codeComplete && <span className="emp-chip">{form.employeeCode}</span>}
                {form.role && <span className="emp-chip">{ROLE_LABEL[form.role] || form.role}</span>}
                {form.department && <span className="emp-chip">{DEPT_LABEL[form.department] || form.department}</span>}
              </div>
            )}
          </div>
          <button type="button" className="emp-modal-close" onClick={closeForm} aria-label="Close" disabled={submitting}>
            <CloseIcon />
          </button>
        </header>

        <div className="emp-modal-body">

          <section className="emp-section">
            <div>
              <h3 className="emp-section-title">Personal details</h3>
              <p className="emp-section-desc">The name and contact details used across the portal.</p>
            </div>
            <div className="emp-grid">
              <Field label="Full name" htmlFor="emp-name" required error={formErr.fullName} full>
                <input id="emp-name" type="text" name="fullName" value={form.fullName} onChange={onChange}
                  placeholder="e.g. Suganya K" className={inputCls('fullName')} autoFocus={!editMode} />
              </Field>
              <Field label="Official email" htmlFor="emp-email" required error={formErr.email}
                hint={editMode ? 'Email can’t be changed after creation' : '@manurevasolutions.net or .com'} full>
                <input id="emp-email" type="email" name="email" value={form.email} onChange={onChange}
                  placeholder="name@manurevasolutions.net" className={inputCls('email')} disabled={editMode} />
              </Field>
              <Field label="Phone number" htmlFor="emp-phone" required error={formErr.phoneNumber}>
                <input id="emp-phone" type="tel" name="phoneNumber" value={form.phoneNumber} onChange={onChange}
                  placeholder="10-digit mobile" className={inputCls('phoneNumber')} maxLength={10} inputMode="numeric" />
              </Field>
              <Field label="Alternate phone" htmlFor="emp-phone2" error={formErr.alternatePhone} hint="Optional">
                <input id="emp-phone2" type="tel" name="alternatePhone" value={form.alternatePhone} onChange={onChange}
                  placeholder="10-digit mobile" className={inputCls('alternatePhone')} maxLength={10} inputMode="numeric" />
              </Field>
            </div>
          </section>

          <section className="emp-section">
            <div>
              <h3 className="emp-section-title">Employment</h3>
              <p className="emp-section-desc">Role decides what they can access and who approves their leave.</p>
            </div>
            <div className="emp-grid">
              <Field
                label="Employee code" htmlFor="emp-code" required
                error={formErr.employeeCode || (codeTaken ? 'Code already taken' : '')}
                ok={codeComplete && !codeTaken ? 'Code available' : ''}
                hint="MA followed by numbers, e.g. MA001"
              >
                <div className="emp-input-wrap">
                  <input id="emp-code" type="text" name="employeeCode" value={form.employeeCode} onChange={onCodeChange}
                    placeholder="MA001" autoComplete="off" maxLength={10}
                    className={inputCls('employeeCode', `emp-input--code${codeTaken ? ' has-error' : codeComplete ? ' is-valid' : ''}`)} />
                  {codeComplete && (
                    <span className="emp-input-icon">
                      {codeTaken ? (
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#dc2626" strokeWidth="2" fill="#fef2f2"/><line x1="15" y1="9" x2="9" y2="15" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/><line x1="9" y1="9" x2="15" y2="15" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/></svg>
                      ) : (
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#16a34a" strokeWidth="2" fill="#f0fdf4"/><polyline points="8 12 11 15 16 9" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      )}
                    </span>
                  )}
                </div>
              </Field>
              <Field label="Joining date" htmlFor="emp-join" required error={formErr.join_date}>
                <input id="emp-join" type="date" name="join_date" value={form.join_date} onChange={onChange}
                  className={inputCls('join_date')} max="9999-12-31" />
              </Field>
              <Field label="Role" htmlFor="emp-role" required error={formErr.role}>
                <select id="emp-role" name="role" value={form.role} onChange={onRoleChange} className={inputCls('role')}>
                  <option value="">Select role</option>
                  {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label="Department" htmlFor="emp-dept" required error={formErr.department}>
                <select id="emp-dept" name="department" value={form.department} onChange={onChange} className={inputCls('department')}>
                  <option value="">Select department</option>
                  {DEPT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>

              {showManager && (
                <Field label="Reporting manager" htmlFor="emp-mgr" full
                  hint={assignableManagers.length === 0 ? 'No managers yet. Create a Manager or Superadmin first.' : 'Approves this person’s leave requests'}>
                  <select id="emp-mgr" name="manager" value={form.manager} onChange={onChange} className="emp-input">
                    <option value="">No manager</option>
                    {assignableManagers.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
                  </select>
                </Field>
              )}
              {showHR && (
                <Field label="Assigned HR" htmlFor="emp-hr" full
                  hint={assignableHR.length === 0 ? 'No HR users yet. Create an HR user first.' : 'Approves this consultant’s leave requests'}>
                  <select id="emp-hr" name="manager" value={form.manager} onChange={onChange} className="emp-input">
                    <option value="">No HR</option>
                    {assignableHR.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
                  </select>
                </Field>
              )}
            </div>
          </section>

          <section className="emp-section">
            <div>
              <h3 className="emp-section-title">Address</h3>
              <p className="emp-section-desc">State is used for regional holidays and reports.</p>
            </div>
            <div className="emp-grid">
              <Field label="Street address" htmlFor="emp-address" required error={formErr.address} full>
                <textarea id="emp-address" name="address" value={form.address} onChange={onChange}
                  placeholder="Door no, street, area, city, PIN" rows={2} className={inputCls('address')} />
              </Field>
              <Field label="Country" htmlFor="emp-country" required error={formErr.location}>
                <SearchSelect
                  id="emp-country"
                  value={form.location}
                  options={COUNTRIES}
                  onChange={onCountryChange}
                  placeholder="Select country"
                  searchPlaceholder="Search country…"
                  emptyText="No country found"
                  hasError={!!formErr.location}
                />
              </Field>
              <Field label={isIndia ? 'State / UT' : 'State / province'} htmlFor="emp-state" required error={formErr.state}>
                {isIndia ? (
                  <SearchSelect
                    id="emp-state"
                    value={form.state}
                    options={INDIAN_STATES}
                    onChange={onStateChange}
                    placeholder="Select state"
                    searchPlaceholder="Search state…"
                    emptyText="No state found"
                    hasError={!!formErr.state}
                  />
                ) : (
                  <input id="emp-state" type="text" name="state" value={form.state}
                    onChange={e => onStateChange(e.target.value)}
                    placeholder={form.location ? 'Enter state or province' : 'Select a country first'}
                    disabled={!form.location} className={inputCls('state')} />
                )}
              </Field>
            </div>
          </section>
        </div>

        <footer className="emp-modal-footer">
          <span className="emp-footer-note"><span className="emp-req">*</span> Required fields</span>
          <div className="emp-footer-actions">
            <button type="button" className="emp-btn emp-btn--ghost" onClick={closeForm} disabled={submitting}>Cancel</button>
            <button type="button" className="emp-btn emp-btn--primary" onClick={handleSubmit} disabled={submitting || codeTaken}>
              {submitting && <span className="emp-spinner" aria-hidden="true" />}
              {submitting ? 'Saving…' : editMode ? 'Save changes' : 'Add employee'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default EmployeeFormModal;
